import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { useContentData } from '../useContentData'
import { supabase } from '@/lib/supabase'
import {
    Share2,
    Globe,
    Copy,
    ExternalLink,
    Code,
    Loader2,
    HelpCircle,
    Calendar,
    Send,
    Clock,
    X,
    Edit2,
    CheckCircle,
    AlertCircle,
    FileText,
    Eye,
    AlertTriangle
} from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ShareContentDropdownProps } from '../utils/contentTypes'
import { getStaticContentUrl } from '../utils/contentUtils'
import { useToast } from '@/components/ui/use-toast'
import { ChangeScheduleModal } from './ChangeScheduleModal'
import { format } from 'date-fns'

/**
 * ShareContentDropdown component for sharing and publishing content
 */
export function ShareContentDropdown({ 
    contentId, 
    contentType, 
    versionNumber,
    onShowPublishHelp,
    onContentUpdate
}: ShareContentDropdownProps) {
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
    const [isPublishing, setIsPublishing] = useState<boolean>(false)
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState<boolean>(false)
    const [isNewSchedule, setIsNewSchedule] = useState<boolean>(false)
    const [scheduledPost, setScheduledPost] = useState<any>(null)
    const [isCheckingSchedule, setIsCheckingSchedule] = useState<boolean>(false)
    const [isPlatformConnected, setIsPlatformConnected] = useState<boolean>(false)
    const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(true)
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false)
    const [showUnpublishDialog, setShowUnpublishDialog] = useState<boolean>(false)
    
    const { toast } = useToast()
    const {
        currentContent,
        fetchContentDetails,
        fetchScheduledPostStatus,
        publishContentNow,
        cancelScheduledPost,
        unpublishContent,
        updateContentDetails
    } = useContentData()
    
    // Force refresh content data when dropdown opens
    useEffect(() => {
        if (isDropdownOpen && contentId) {
            // Refresh content details
            fetchContentDetails(contentId)
                .then(content => {
                    if(!content) {
                        toast({
                            title: "Error",
                            description: "Content not found",
                            variant: "destructive"
                        })
                    }
                    return content
                })
                .then(async (content) => {
                    // Check platform connection status
                    if (content?.platform_key && content?.brand_id) {
                        setIsCheckingConnection(true)
                        try {
                            // First get the platform ID
                            const { data: platform } = await supabase
                                .from('platforms')
                                .select('id')
                                .eq('platform_key', content.platform_key)
                                .single()

                            if (platform) {
                                // Then check if platform is connected
                                const { data: connection } = await supabase
                                    .from('platform_brand_settings')
                                    .select('status')
                                    .eq('brand_id', content.brand_id)
                                    .eq('platform_id', platform.id)
                                    .single()

                                setIsPlatformConnected(connection?.status === 'CONNECTED')
                            } else {
                                setIsPlatformConnected(false)
                            }
                        } catch (error) {
                            console.error('Error checking platform connection:', error)
                            setIsPlatformConnected(false)
                        } finally {
                            setIsCheckingConnection(false)
                        }
                    } else {
                        setIsPlatformConnected(false)
                        setIsCheckingConnection(false)
                    }
                })
                .catch(err => {
                    console.error('Error refreshing content on dropdown open:', err)
                    setIsCheckingConnection(false)
                })
            
            // Check for existing scheduled post
            setIsCheckingSchedule(true)
            fetchScheduledPostStatus(contentId)
                .then(status => {
                    setScheduledPost(status)
                })
                .catch(err => {
                    console.error('Error checking scheduled post:', err)
                })
                .finally(() => {
                    setIsCheckingSchedule(false)
                })
        }
    }, [isDropdownOpen, contentId, fetchContentDetails, fetchScheduledPostStatus])
    
    // Helper function to determine if we're viewing the current version
    const isViewingCurrentVersion = () => {
        if (!currentContent) return false
        if (versionNumber === undefined) return true // Not viewing a specific version = viewing current
        return versionNumber === currentContent.current_version
    }
    
    // Use useMemo to recalculate when dependencies change
    const showingCurrentVersion = useMemo(() => {
        return isViewingCurrentVersion()
    }, [currentContent, versionNumber])
    
    const handleCopyUrl = (type: string) => {
        const url = getStaticContentUrl(contentId, type, versionNumber)
        navigator.clipboard.writeText(url)
        toast({
            title: "URL Copied",
            description: `Shareable URL copied to clipboard`,
        })
    }
    
    const handleOpenPreview = (type: string) => {
        window.open(getStaticContentUrl(contentId, type, versionNumber), '_blank')
    }
    
    // Handle immediate publishing
    const handlePublishNow = async () => {
        setIsPublishing(true)
        
        try {
            const result = await publishContentNow(contentId)
            
            if (result.success) {
                toast({
                    title: "Success",
                    description: "Content published successfully",
                })
                
                // Refresh content and scheduled post status
                await fetchContentDetails(contentId)
                const status = await fetchScheduledPostStatus(contentId)
                setScheduledPost(status)
                
                if (onContentUpdate) {
                    onContentUpdate()
                }
            } else {
                toast({
                    title: "Error",
                    description: result.error || "Failed to publish content",
                    variant: "destructive"
                })
            }
        } catch (error) {
            console.error('Error publishing content:', error)
            toast({
                title: "Error",
                description: "An unexpected error occurred while publishing",
                variant: "destructive"
            })
        } finally {
            setIsPublishing(false)
        }
    }
    
    // Handle publishing now instead of scheduled
    const handlePublishNowInstead = async () => {
        if (!scheduledPost) return
        
        setIsPublishing(true)
        
        try {
            // First cancel the scheduled post
            await cancelScheduledPost(contentId)
            
            // Then publish immediately
            const result = await publishContentNow(contentId)
            
            if (result.success) {
                toast({
                    title: "Success",
                    description: "Content published immediately",
                })
                
                // Refresh content and scheduled post status
                await fetchContentDetails(contentId)
                const status = await fetchScheduledPostStatus(contentId)
                setScheduledPost(status)
                
                if (onContentUpdate) {
                    onContentUpdate()
                }
            } else {
                toast({
                    title: "Error",
                    description: result.error || "Failed to publish content",
                    variant: "destructive"
                })
            }
        } catch (error) {
            console.error('Error publishing content:', error)
            toast({
                title: "Error",
                description: "An unexpected error occurred while publishing",
                variant: "destructive"
            })
        } finally {
            setIsPublishing(false)
        }
    }
    
    // Handle canceling scheduled post
    const handleCancelSchedule = async () => {
        if (!scheduledPost) return

        setIsPublishing(true)

        try {
            const result = await cancelScheduledPost(contentId)

            if (result.success) {
                toast({
                    title: "Success",
                    description: "Scheduled post cancelled",
                })

                // Refresh scheduled post status
                setScheduledPost(null)

                if (onContentUpdate) {
                    onContentUpdate()
                }
            } else {
                toast({
                    title: "Error",
                    description: result.error || "Failed to cancel schedule",
                    variant: "destructive"
                })
            }
        } catch (error) {
            console.error('Error canceling schedule:', error)
            toast({
                title: "Error",
                description: "An unexpected error occurred",
                variant: "destructive"
            })
        } finally {
            setIsPublishing(false)
        }
    }

    // Show unpublish warning dialog
    const handleUnpublishClick = () => {
        setShowUnpublishDialog(true)
    }

    // Handle unpublishing content after confirmation
    const handleConfirmUnpublish = async () => {
        if (!scheduledPost) return

        setShowUnpublishDialog(false)
        setIsPublishing(true)

        try {
            const result = await unpublishContent(contentId)

            if (result.success) {
                toast({
                    title: "Success",
                    description: "Content unpublished successfully",
                })

                // Refresh content and scheduled post status
                await fetchContentDetails(contentId)
                const status = await fetchScheduledPostStatus(contentId)
                setScheduledPost(status)

                if (onContentUpdate) {
                    onContentUpdate()
                }
            } else {
                toast({
                    title: "Error",
                    description: result.error || "Failed to unpublish content",
                    variant: "destructive"
                })
            }
        } catch (error) {
            console.error('Error unpublishing content:', error)
            toast({
                title: "Error",
                description: "An unexpected error occurred while unpublishing",
                variant: "destructive"
            })
        } finally {
            setIsPublishing(false)
        }
    }
    
    // Handle opening schedule modal
    const handleOpenScheduleModal = (isNew: boolean) => {
        setIsNewSchedule(isNew)
        setIsScheduleModalOpen(true)
    }
    
    // Handle schedule update from modal
    const handleScheduleUpdate = async () => {
        // Refresh scheduled post status
        const status = await fetchScheduledPostStatus(contentId)
        setScheduledPost(status)
        
        if (onContentUpdate) {
            onContentUpdate()
        }
    }
    
    // Handler for dropdown open state change
    const handleOpenChange = (open: boolean) => {
        setIsDropdownOpen(open)
    }
    
    // Handle status toggle between DRAFT and READY
    const handleStatusToggle = async () => {
        if (!currentContent || isUpdatingStatus) return
        
        const newStatus = currentContent.status === 'DRAFT' ? 'READY' : 'DRAFT'
        setIsUpdatingStatus(true)
        
        try {
            await updateContentDetails(contentId, { status: newStatus })
            
            toast({
                title: "Status Updated",
                description: `Content status changed to ${newStatus === 'DRAFT' ? 'Draft' : 'Ready'}`,
            })
            
            // Refresh content details
            await fetchContentDetails(contentId)
            
            if (onContentUpdate) {
                onContentUpdate()
            }
        } catch (error) {
            console.error('Error updating content status:', error)
            toast({
                title: "Error",
                description: "Failed to update content status",
                variant: "destructive"
            })
        } finally {
            setIsUpdatingStatus(false)
        }
    }
    
    // Determine if content has a platform set
    const hasPlatform = currentContent?.platform_key || currentContent?.content_json?.platform
    
    // Check if scheduled post exists and its status
    const isScheduled = scheduledPost && (scheduledPost.status === 'scheduled' || scheduledPost.status === 'SCHEDULED')
    const isPublished = scheduledPost && (scheduledPost.status === 'published' || scheduledPost.status === 'PUBLISHED')
    
    // Check if we should show the status toggle
    const shouldShowStatusToggle = showingCurrentVersion && 
        currentContent && 
        (currentContent.status === 'DRAFT' || currentContent.status === 'READY')
    
    // Get current status info for display
    const currentStatus = currentContent?.status
    const isDraftStatus = currentStatus === 'DRAFT'
    
    // Determine which options to show based on content type
    const showJsOption = contentType === 'tool'
    const showCssOption = contentType === 'tool'
    
    return (
        <>
            <DropdownMenu open={isDropdownOpen} onOpenChange={handleOpenChange}>
                <DropdownMenuTrigger asChild>
                    <Button variant="default" disabled={isPublishing}>
                        {isPublishing ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Publishing...
                            </>
                        ) : (
                            <>
                                <Share2 className="h-4 w-4 mr-2" />
                                Share & Publish
                            </>
                        )}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80">
                    <DropdownMenuItem onClick={onShowPublishHelp} className="py-2 px-3">
                        <HelpCircle className="h-4 w-4 mr-2" />
                        <span>How do I publish this?</span>
                    </DropdownMenuItem>

                    {/* Status Toggle Section */}
                    {shouldShowStatusToggle && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-muted-foreground py-2 px-3">
                                Content Status
                            </DropdownMenuLabel>
                            
                            <DropdownMenuItem 
                                onClick={handleStatusToggle}
                                disabled={isUpdatingStatus}
                                className="py-2 px-3"
                            >
                                {isUpdatingStatus ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        <span>Updating...</span>
                                    </>
                                ) : isDraftStatus ? (
                                    <>
                                        <Eye className="h-4 w-4 mr-2" />
                                        <span>Mark as Ready</span>
                                    </>
                                ) : (
                                    <>
                                        <FileText className="h-4 w-4 mr-2" />
                                        <span>Mark as Draft</span>
                                    </>
                                )}
                            </DropdownMenuItem>
                            
                            {/* Current status indicator */}
                            <DropdownMenuItem disabled className="py-2 px-3">
                                <div className="flex items-center gap-2 w-full text-xs text-muted-foreground">
                                    <div className={`w-2 h-2 rounded-full ${
                                        isDraftStatus ? 'bg-blue-500' : 'bg-yellow-500'
                                    }`} />
                                    <span>Currently: {isDraftStatus ? 'Draft' : 'Ready'}</span>
                                </div>
                            </DropdownMenuItem>
                            
                            {/* Draft mode explanation */}
                            {isDraftStatus && (
                                <DropdownMenuItem disabled className="py-2 px-3">
                                    <div className="text-xs text-muted-foreground leading-relaxed">
                                        <AlertCircle className="h-3 w-3 mr-1 inline" />
                                        Content in draft mode will not be available or used in any automatic content scheduling
                                    </div>
                                </DropdownMenuItem>
                            )}
                        </>
                    )}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={() => handleOpenPreview('html')} className="py-2 px-3">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        <span>Open in New Tab</span>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem onClick={() => handleCopyUrl('html')} className="py-2 px-3">
                        <Copy className="h-4 w-4 mr-2" />
                        <span>Copy Shareable URL</span>
                    </DropdownMenuItem>
                    
                    {/* Show publishing options only when viewing the current version and content is not in draft */}
                    {showingCurrentVersion && hasPlatform && !isDraftStatus && (
                        <>
                            <DropdownMenuSeparator />
                            
                            {isCheckingSchedule ? (
                                <DropdownMenuItem disabled className="py-2 px-3">
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    <span>Checking schedule status...</span>
                                </DropdownMenuItem>
                            ) : isPublished ? (
                                <>
                                    <DropdownMenuLabel className="text-xs text-muted-foreground py-2 px-3">
                                        Published Content
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem disabled className="py-2 px-3">
                                        <div className="flex items-center gap-2 w-full">
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                            <span className="flex-1">Published</span>
                                            {scheduledPost?.published_at && (
                                                <span className="text-xs text-muted-foreground">
                                                    {format(new Date(scheduledPost.published_at), 'MMM d, h:mm a')}
                                                </span>
                                            )}
                                        </div>
                                    </DropdownMenuItem>
                                    {scheduledPost?.published_url && (
                                        <DropdownMenuItem
                                            onClick={() => window.open(scheduledPost.published_url, '_blank')}
                                            className="py-2 px-3 text-blue-600"
                                        >
                                            <ExternalLink className="h-4 w-4 mr-2" />
                                            <span>View Published Post</span>
                                        </DropdownMenuItem>
                                    )}

                                    <DropdownMenuItem
                                        onClick={handleUnpublishClick}
                                        disabled={isPublishing}
                                        className="py-2 px-3 text-destructive"
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        <span>Unpublish</span>
                                    </DropdownMenuItem>
                                </>
                            ) : isScheduled ? (
                                <>
                                    <DropdownMenuLabel className="text-xs text-muted-foreground py-2 px-3">
                                        Scheduled Post
                                    </DropdownMenuLabel>
                                    <DropdownMenuItem disabled className="py-2 px-3">
                                        <div className="flex items-center gap-2 w-full">
                                            <Clock className="h-4 w-4 text-blue-600" />
                                            <span className="flex-1">Scheduled for</span>
                                            {scheduledPost?.scheduled_for && (
                                                <span className="text-xs font-medium">
                                                    {format(new Date(scheduledPost.scheduled_for), 'MMM d, h:mm a')}
                                                </span>
                                            )}
                                        </div>
                                    </DropdownMenuItem>
                                    
                                    <DropdownMenuItem 
                                        onClick={() => handleOpenScheduleModal(false)}
                                        className="py-2 px-3"
                                    >
                                        <Edit2 className="h-4 w-4 mr-2" />
                                        <span>Reschedule</span>
                                    </DropdownMenuItem>
                                    
                                    <DropdownMenuItem 
                                        onClick={handlePublishNowInstead}
                                        disabled={isPublishing}
                                        className="py-2 px-3"
                                    >
                                        <Send className="h-4 w-4 mr-2" />
                                        <span>Publish Now Instead</span>
                                    </DropdownMenuItem>
                                    
                                    <DropdownMenuItem 
                                        onClick={handleCancelSchedule}
                                        disabled={isPublishing}
                                        className="py-2 px-3 text-destructive"
                                    >
                                        <X className="h-4 w-4 mr-2" />
                                        <span>Cancel Schedule</span>
                                    </DropdownMenuItem>
                                </>
                            ) : (
                                <>
                                    <DropdownMenuLabel className="text-xs text-muted-foreground py-2 px-3">
                                        Publishing Options
                                    </DropdownMenuLabel>
                                    
                                    <DropdownMenuItem 
                                        onClick={handlePublishNow}
                                        disabled={isPublishing || !isPlatformConnected || isCheckingConnection}
                                        className="py-2 px-3"
                                    >
                                        {isCheckingConnection ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                <span>Checking connection...</span>
                                            </>
                                        ) : !isPlatformConnected ? (
                                            <>
                                                <AlertCircle className="h-4 w-4 mr-2" />
                                                <span>Publish Now (Not connected)</span>
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4 mr-2" />
                                                <span>Publish Now</span>
                                            </>
                                        )}
                                    </DropdownMenuItem>
                                    
                                    <DropdownMenuItem 
                                        onClick={() => handleOpenScheduleModal(true)}
                                        disabled={!isPlatformConnected || isCheckingConnection}
                                        className="py-2 px-3"
                                    >
                                        {!isPlatformConnected ? (
                                            <>
                                                <AlertCircle className="h-4 w-4 mr-2" />
                                                <span>Schedule for Later (Not connected)</span>
                                            </>
                                        ) : (
                                            <>
                                                <Calendar className="h-4 w-4 mr-2" />
                                                <span>Schedule for Later</span>
                                            </>
                                        )}
                                    </DropdownMenuItem>
                                </>
                            )}
                        </>
                    )}
                    
                    {/* Show message if content is in draft mode */}
                    {showingCurrentVersion && hasPlatform && isDraftStatus && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled className="py-2 px-3">
                                <div className="text-sm text-muted-foreground">
                                    <FileText className="h-4 w-4 mb-1" />
                                    Content must be marked as "Ready" before publishing
                                </div>
                            </DropdownMenuItem>
                        </>
                    )}
                    
                    {/* Show message if no platform is set */}
                    {showingCurrentVersion && !hasPlatform && !isDraftStatus && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem disabled className="py-2 px-3">
                                <div className="text-sm text-muted-foreground">
                                    <Globe className="h-4 w-4 mb-1" />
                                    No platform specified for this content
                                </div>
                            </DropdownMenuItem>
                        </>
                    )}
                    
                    {/* JS/CSS options */}
                    {(showJsOption || showCssOption) && <DropdownMenuSeparator />}
                    
                    {showJsOption && (
                        <DropdownMenuItem onClick={() => handleCopyUrl('js')} className="py-2 px-3">
                            <Code className="h-4 w-4 mr-2" />
                            <span>Copy JS URL</span>
                        </DropdownMenuItem>
                    )}
                    
                    {showCssOption && (
                        <DropdownMenuItem onClick={() => handleCopyUrl('css')} className="py-2 px-3">
                            <Code className="h-4 w-4 mr-2" />
                            <span>Copy CSS URL</span>
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Schedule Modal */}
            <ChangeScheduleModal
                isOpen={isScheduleModalOpen}
                onClose={() => setIsScheduleModalOpen(false)}
                contentId={contentId}
                currentScheduledTime={scheduledPost?.scheduled_for}
                onScheduleUpdate={handleScheduleUpdate}
                isNewSchedule={isNewSchedule}
            />

            {/* Unpublish Warning Dialog */}
            <AlertDialog open={showUnpublishDialog} onOpenChange={setShowUnpublishDialog}>
                <AlertDialogContent className="sm:max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Unpublish Content?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-left space-y-3 pt-2">
                            <p className="font-medium text-foreground">
                                Warning: This action may not be reversible on some platforms.
                            </p>
                            <p>
                                While this will remove the post from the platform, some platforms (like LinkedIn) may prevent you from republishing the same content due to duplicate detection.
                            </p>
                            <p>
                                If republishing fails, you may need to make edits to the content before trying again.
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Are you sure you want to unpublish this content?
                            </p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                        <AlertDialogCancel className="w-full sm:w-auto">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmUnpublish}
                            disabled={isPublishing}
                            className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isPublishing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Unpublishing...
                                </>
                            ) : (
                                'Yes, Unpublish'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}